/**
 * Corrigir estoque — porte do fluxo homônimo do Themis 1.x.
 *
 * Três fases, e **nenhuma delas é opcional**:
 *
 *  1. BUSCAR ANTES. Lê o saldo atual no ERP e grava em `estoqueSistema`. Sem isto, a
 *     comparação usaria o saldo da última importação: o app mandaria "corrigir" itens que
 *     já batiam e deixaria passar divergências surgidas desde então.
 *
 *  2. ENVIAR. Só os itens divergentes. Item que bateu não tem o que corrigir, e mandar
 *     todos seriam 2000 requisições para resolver 40 problemas.
 *
 *  3. VERIFICAR DEPOIS. Espera o ERP processar, lê o saldo de novo e confere item a item
 *     se ficou igual ao que foi enviado. O que não refletiu vira pendência com opção de
 *     reenvio — é o "fallback robusto" do 1.x. Sem ele, um envio aceito pelo ERP mas não
 *     aplicado passa despercebido, e o estoque fica errado achando que foi corrigido.
 *
 * O fechamento confere só o que bateu e o que o ERP aceitou (ou o recusado que a releitura
 * mostra aplicado). Divergente que o ERP recusou, sem `IdProduto` ou fora do ERP continua
 * ATUALIZADO: aparece na mensagem final e volta na próxima correção (o `diagnosticar` pega só
 * ATUALIZADO). Conferir o recusado o tiraria da lista de trabalho com o ERP ainda errado.
 *
 * Exige conexão. Offline, os lotes do Firestore ficariam pendentes para sempre.
 */
import {
  fisicoDe,
  idProdutoDe,
  montarEnvio,
  nomeDe,
  saldoDoErpPara,
  saldoNoErp,
  sistemaDe,
  travaAntesDeGravar,
  type Produto,
} from '@themis/shared';
import { atualizarEstoqueSistema, fecharConferencia } from '../../lib/produtos-repo.js';
import { buscarEstoqueDoErp, enviarAoErp } from '../../lib/erp.js';

/** Tempo para o ERP processar antes da releitura de verificação. */
const ESPERA_PROCESSAMENTO_MS = 1500;

/** Pausa entre envios, para não afogar o ERP. Mesmo valor do 1.x. */
const PAUSA_ENTRE_ENVIOS_MS = 500;

export interface Diagnostico {
  /** A loja lida. A correção e o reenvio vão para ela, nunca para a loja aberta no clique. */
  inventoryId: string;
  contados: Produto[];
  /**
   * Contados cuja quantidade difere do saldo do sistema.
   *
   * Inclui os que estão fora do ERP: é esta lista que decide `corrigidoIncorreto` no
   * fechamento da conferência, e ali a divergência existe mesmo que não dê para corrigir.
   * Quem filtra o envio é `paraOErp`, em `executarCorrecao`.
   */
  divergentes: Produto[];
  /**
   * Contados que **não estão na listagem do ERP**, divergentes ou não.
   *
   * Não vão para o envio: mandar quantidade de produto que a loja não tem cadastrado é
   * escrever no ERP a partir de uma comparação contra o saldo da última importação. O ERP
   * recusa ou aceita errado, e a verificação da fase 3 os listaria como pendentes para
   * sempre, sem nunca resolver.
   */
  foraDoErp: Produto[];
  /** Produtos do estoque que o ERP não conhece — contados ou não. */
  semCorrespondencia: number;
  /** Quantos tiveram o saldo do sistema corrigido pela leitura. */
  saldosAtualizados: number;
}

export interface PendenciaErp {
  nome: string;
  enviado: number;
  noSistema: number;
}

/** Marca, em `falhasNoEnvio`, o divergente que não foi enviado por não ter `IdProduto`. */
export const SUFIXO_SEM_ID = ' (sem ID do ERP)';

export interface ResultadoCorrecao {
  /** Aceitos pelo ERP, mais os recusados que a releitura mostrou aplicados. */
  enviados: number;
  /**
   * Divergentes que o ERP recusou (e a releitura não mostrou aplicados), pelo nome, e os sem
   * `IdProduto` como `"<nome> (sem ID do ERP)"`. Nenhum deles é conferido: continuam ATUALIZADO.
   */
  falhasNoEnvio: string[];
  /**
   * Divergentes que ficaram de fora do envio por não estarem na listagem do ERP.
   *
   * Contado à parte de `falhasNoEnvio`: não é falha, é recusa deliberada. Aparece no
   * resultado porque o usuário precisa saber que aqueles itens continuam divergentes no
   * ERP — some daqui e ninguém vai atrás.
   */
  naoEnviadosForaDoErp: string[];
  /** Itens cujo saldo no ERP passou a bater com o que foi enviado. */
  confirmados: number;
  /** Enviados que o ERP aceitou mas não refletiu. */
  pendentes: PendenciaErp[];
  conferidos: number;
  divergentesConferidos: number;
  /** A verificação não rodou (falha de leitura). Não é erro de correção. */
  verificacaoIndisponivel: boolean;
  /** Um erro que vale para o laço inteiro parou o envio. `null` quando foi até o fim. */
  interrompido: { motivo: string; naoEnviados: number } | null;
}

const pausa = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Fase 1. Lê o ERP, grava os saldos e devolve o diagnóstico.
 *
 * Separada da execução de propósito: o usuário confirma **com os números certos** na mão,
 * não com a estimativa baseada em dado velho.
 */
export async function diagnosticar(
  inventoryId: string,
  produtos: readonly Produto[],
  aoProgredir: (texto: string) => void,
): Promise<Diagnostico> {
  aoProgredir('Buscando o estoque no ERP');
  const leitura = await buscarEstoqueDoErp(inventoryId);
  if (!leitura.ok) throw new Error(leitura.erro ?? 'Não foi possível ler o estoque do ERP.');
  // Antes de gravar, senão uma listagem errada marca o catálogo inteiro com `apiNotFound` (spec 4.6).
  const trava = travaAntesDeGravar(leitura.itens, leitura.estoque, produtos);
  if (trava) throw new Error(trava);

  aoProgredir('Atualizando os saldos do sistema');
  const sincronia = await atualizarEstoqueSistema(inventoryId, produtos, leitura.estoque, {
    omiteZerados: leitura.omiteZerados,
  });

  // Reaplica o saldo lido na cópia em memória: o listener do Firestore ainda não trouxe
  // a gravação, e o diagnóstico precisa dos números novos agora.
  // Ausente numa listagem que só traz saldo positivo é **zero**, não desconhecido — a
  // mesma regra que `atualizarEstoqueSistema` acabou de gravar. Sem repeti-la aqui, o
  // diagnóstico compararia a contagem com o saldo da última importação para justamente os
  // produtos zerados no ERP, que são os que mais precisam de correção.
  const desconhecidos = new Set<string>();

  const frescos = produtos.map((p) => {
    // Mesma regra que `atualizarEstoqueSistema` acabou de gravar — vem de `shared` para as
    // duas não poderem divergir.
    const atualizado = saldoDoErpPara(leitura.estoque, p, leitura.omiteZerados);
    if (atualizado === undefined) {
      desconhecidos.add(p.id);
      return p;
    }
    return { ...p, estoqueSistema: atualizado };
  });

  const contados = frescos.filter((p) => p.productStatus === 'ATUALIZADO');

  return {
    inventoryId,
    contados,
    divergentes: contados.filter((p) => fisicoDe(p) !== sistemaDe(p)),
    foraDoErp: contados.filter((p) => desconhecidos.has(p.id)),
    semCorrespondencia: sincronia.semCorrespondencia,
    saldosAtualizados: sincronia.atualizados,
  };
}

/** Fases 2 e 3, mais o fechamento da conferência. */
export async function executarCorrecao(
  inventoryId: string,
  diagnostico: Diagnostico,
  aoProgredir: (texto: string, feitos?: number, total?: number) => void,
): Promise<ResultadoCorrecao> {
  const { contados, divergentes, foraDoErp } = diagnostico;

  /**
   * Quem vai ao ERP: divergente, com identificador, e **conhecido pelo ERP**.
   *
   * Produto ausente da listagem fica de fora. Ele não está no estoque daquela loja no ERP,
   * então o número que temos para comparar é o da última importação — mandar uma correção
   * calculada sobre ele é escrever no estoque real a partir de dado velho. E a fase 3 nunca
   * confirmaria: o item continuaria ausente na releitura e viraria pendência eterna.
   */
  const desconhecidos = new Set(foraDoErp.map((p) => p.id));
  const enviaveis = divergentes.filter((p) => !desconhecidos.has(p.id));
  const paraOErp = enviaveis.filter((p) => idProdutoDe(p) != null);

  const naoEnviadosForaDoErp = divergentes.filter((p) => desconhecidos.has(p.id)).map(nomeDe);

  let enviados = 0;
  // Lista de permitidos: só o que o ERP aceitou, ou a releitura mostrou aplicado, entra no fechamento.
  const aceitos = new Set<string>();
  // Quantos de `paraOErp` o laço tratou; o item que o parou não conta.
  let tentados = 0;
  let interrompido: ResultadoCorrecao['interrompido'] = null;

  // ---------- Fase 2: enviar as divergências ----------
  for (const [indice, p] of paraOErp.entries()) {
    aoProgredir('Enviando divergências ao ERP', indice + 1, paraOErp.length);

    // `montarEnvio` monta os sete campos; a API põe o `HashLoja`. Não montar o objeto aqui:
    // foi assim que o payload acabou com metade dos campos e o id em texto.
    const resultado = await enviarAoErp(inventoryId, montarEnvio(p));

    // Sessão, permissão, loja sem hash e servidor sem credencial valem para todos os itens; seguir faria cada um falhar sozinho e sair como recusado pelo ERP.
    if (resultado.parar) {
      interrompido = { motivo: resultado.erro ?? 'Erro no servidor.', naoEnviados: paraOErp.length - indice };
      break;
    }
    tentados++;

    if (resultado.ok) {
      enviados++;
      aceitos.add(p.id);
    }

    if (indice < paraOErp.length - 1) await pausa(PAUSA_ENTRE_ENVIOS_MS);
  }

  // ---------- Fase 3: verificar se o ERP aplicou ----------
  let confirmados = 0;
  const pendentes: PendenciaErp[] = [];
  let verificacaoIndisponivel = false;

  // Roda mesmo com tudo recusado: o ERP às vezes aplica o que a API deu como falha (estouro,
  // 504 do proxy), e só a releitura mostra.
  if (interrompido) {
    // O erro que parou o envio pararia a releitura também: vale a regra da releitura indisponível.
    verificacaoIndisponivel = enviados > 0;
  } else if (paraOErp.length > 0) {
    aoProgredir('Aguardando o ERP processar');
    await pausa(ESPERA_PROCESSAMENTO_MS);

    aoProgredir('Conferindo se a correção foi aplicada');
    const releitura = await buscarEstoqueDoErp(inventoryId);

    // Releitura vazia é hash errado ou ERP fora, não "nada refletiu".
    if (!releitura.ok || travaAntesDeGravar(releitura.itens, releitura.estoque, contados) !== null) {
      // Não conseguir verificar não invalida o envio; só não dá para afirmar que deu certo.
      // Com tudo recusado não houve envio aceito a afirmar: a lista de recusados já diz o que fazer.
      verificacaoIndisponivel = enviados > 0;
    } else {
      for (const p of paraOErp) {
        // O valor **como foi enviado** (inteiro, nunca negativo), não o físico cru: é ele
        // que o ERP recebeu, e é contra ele que a comparação faz sentido.
        const enviado = montarEnvio(p).Quantidade;
        const noSistema = saldoNoErp(releitura.estoque, p);

        if (!aceitos.has(p.id)) {
          // Recusado com o saldo enviado no ERP: aplicado, conta como aceito. Sem isto ele
          // ficava ATUALIZADO e a correção seguinte o conferia como "bateu", sem a divergência.
          // Com o saldo antigo, não é "aceito mas não refletiu": fica fora de pendentes e do reenvio.
          if (noSistema === enviado) {
            aceitos.add(p.id);
            enviados++;
            confirmados++;
          }
        } else if (noSistema === undefined) {
          pendentes.push({ nome: nomeDe(p), enviado, noSistema: NaN });
        } else if (noSistema === enviado) {
          confirmados++;
        } else {
          pendentes.push({ nome: nomeDe(p), enviado, noSistema });
        }
      }

      await atualizarEstoqueSistema(inventoryId, contados, releitura.estoque);
    }
  }

  // Depois da fase 3, que pode ter achado aplicado um recusado.
  const falhasNoEnvio = [
    ...paraOErp.slice(0, tentados).filter((p) => !aceitos.has(p.id)).map(nomeDe),
    // Sem `IdProduto` não há o que enviar, mas a divergência existe: avisa em vez de conferir.
    ...enviaveis.filter((p) => idProdutoDe(p) == null).map((p) => `${nomeDe(p)}${SUFIXO_SEM_ID}`),
  ];

  // ---------- Fechamento da conferência ----------
  aoProgredir('Fechando a conferência');
  const idsDivergentes = new Set(divergentes.map((p) => p.id));
  const paraConferir = contados.filter((p) => !idsDivergentes.has(p.id) || aceitos.has(p.id));
  const fechados = await fecharConferencia(inventoryId, paraConferir, idsDivergentes);

  return {
    enviados,
    falhasNoEnvio,
    naoEnviadosForaDoErp,
    confirmados,
    pendentes,
    conferidos: fechados.divergentes + fechados.corretos,
    divergentesConferidos: fechados.divergentes,
    verificacaoIndisponivel,
    interrompido,
  };
}

/** Reenvia o que não refletiu, para o caso de ter sido só demora do ERP. */
export async function reenviarPendentes(
  inventoryId: string,
  produtos: readonly Produto[],
  pendentes: readonly PendenciaErp[],
  aoProgredir: (feitos: number, total: number) => void,
): Promise<{ reenviados: number; interrompido: string | null }> {
  const porNome = new Map(produtos.map((p) => [nomeDe(p), p]));
  let reenviados = 0;

  for (const [indice, pendencia] of pendentes.entries()) {
    aoProgredir(indice + 1, pendentes.length);

    const p = porNome.get(pendencia.nome);
    if (!p) continue;

    // A quantidade enviada da primeira vez, não a atual: é ela que precisa entrar.
    const resultado = await enviarAoErp(inventoryId, montarEnvio(p, pendencia.enviado));

    // Mesmo motivo do laço da correção: o erro vale para todos, não adianta seguir.
    if (resultado.parar) return { reenviados, interrompido: resultado.erro ?? 'Erro no servidor.' };
    if (resultado.ok) reenviados++;
    if (indice < pendentes.length - 1) await pausa(PAUSA_ENTRE_ENVIOS_MS);
  }

  return { reenviados, interrompido: null };
}
